import React, { useState } from 'react';
import {
    Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
    TextField, Select, MenuItem, FormControl, InputLabel, Button, Pagination, Grid, Typography,
    Dialog, DialogTitle, DialogContent, DialogActions, List, ListItem, ListItemText, ListItemIcon, Divider
} from '@mui/material';
import { Description as FileIcon, FolderOpen as FolderIcon, OpenInNew as OpenIcon } from '@mui/icons-material';
import { getConfig } from '../adm/ConfigContext';

const mesesNumeros = {
    janeiro: 1, fevereiro: 2, marco: 3, abril: 4, maio: 5, junho: 6, julho: 7, agosto: 8, setembro: 9, outubro: 10, novembro: 11, dezembro: 12,
};

const months = [
    { value: '', label: 'Todos' },
    { value: 'janeiro', label: 'Janeiro' },
    { value: 'fevereiro', label: 'Fevereiro' },
    { value: 'marco', label: 'Março' },
    { value: 'abril', label: 'Abril' },
    { value: 'maio', label: 'Maio' },
    { value: 'junho', label: 'Junho' },
    { value: 'julho', label: 'Julho' },
    { value: 'agosto', label: 'Agosto' },
    { value: 'setembro', label: 'Setembro' },
    { value: 'outubro', label: 'Outubro' },
    { value: 'novembro', label: 'Novembro' },
    { value: 'dezembro', label: 'Dezembro' },
];

export default function DataTable({ data }) {
    const config = getConfig();
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [selectedYear, setSelectedYear] = useState('');
    const [selectedMonth, setSelectedMonth] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedModality, setSelectedModality] = useState('');

    // Estados do Dialog / Pop-up para múltiplos arquivos de "Outros"
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogFiles, setDialogFiles] = useState([]);
    const [dialogTitle, setDialogTitle] = useState('');

    const handleModalityChange = (e) => setSelectedModality(e.target.value);
    const handlePageChange = (_, value) => setCurrentPage(value);
    const handleItemsPerPageChange = (e) => setItemsPerPage(parseInt(e.target.value, 10));
    const handleYearChange = (e) => setSelectedYear(e.target.value);
    const handleMonthChange = (e) => setSelectedMonth(e.target.value);
    const handleSearchChange = (e) => setSearchTerm(e.target.value);

    const uniqueYears = [...new Set(data.map(item => String(item.data?.split('-')[0])))]
        .filter(Boolean)
        .sort((a, b) => b.localeCompare(a));

    const filteredAndSortedData = () => {
        let filteredItems = data.map(item => ({
            ...item,
            ano: item.data ? String(item.data.split('-')[0]) : '',
            mes: item.data ? Object.keys(mesesNumeros).find(m => mesesNumeros[m] === parseInt(item.data.split('-')[1])) : ''
        }));

        filteredItems = filteredItems.filter(item =>
            (!selectedYear || String(item.ano) === String(selectedYear)) &&
            (!selectedMonth || item.mes?.toLowerCase() === selectedMonth.toLowerCase()) &&
            (!searchTerm || item.title?.toLowerCase().includes(searchTerm.toLowerCase()) || item.numero?.toLowerCase().includes(searchTerm.toLowerCase())) &&
            (!selectedModality || item.modalidade === selectedModality)
        );

        return filteredItems.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
    };

    const displayedData = filteredAndSortedData();

    const modalidadeConst = [
        'PREGÃO ELETRÔNICO', 'PREGÃO PRESENCIAL', 'TOMADA DE PREÇOS', "DISPENSA",
        "CHAMADA PÚBLICA", "CONCORRÊNCIA", "CHAMAMENTO PÚBLICO", "LEILÃO",
        "DIÁLOGO COMPETITIVO", "CONCURSO", "CREDENCIAMENTO", "ADESÃO", "INEXIGIBILIDADE"
    ];

    const docLabels = {
        edital: 'EDITAL',
        dfd: 'DFD',
        etp: 'ETP',
        pesquisaMercado: 'PESQ. MERCADO',
        autorizacao: 'AUTORIZAÇÃO',
        proposta: 'PROPOSTA',
        contrato: 'CONTRATO',
        documentacao: 'DOCUMENTAÇÃO',
        projetoBasico: 'PROJETO BÁSICO'
    };

    const handleOutrosClick = (outrosArray, licitacaoTitulo) => {
        if (!outrosArray || outrosArray.length === 0) return;

        if (outrosArray.length === 1) {
            window.open(outrosArray[0].link, '_blank');
        } else {
            setDialogFiles(outrosArray);
            setDialogTitle(licitacaoTitulo || 'Documentos Adicionais');
            setDialogOpen(true);
        }
    };

    // Cor padrão Gov.br (Azul institucional) caso o config não traga
    const govHeaderColor = config?.color || '#1351B4';

    return (
        <Box sx={{ width: '100%', mx: 'auto' }}>
            {/* Filtros com estilo institucional e bordas retas/modernas */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={3}>
                    <TextField
                        fullWidth
                        label="Buscar por nome ou número"
                        variant="outlined"
                        value={searchTerm}
                        onChange={handleSearchChange}
                        size="small"
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '4px' } }}
                    />
                </Grid>
                <Grid item xs={12} sm={2}>
                    <FormControl fullWidth variant="outlined" size="small">
                        <InputLabel>Itens por página</InputLabel>
                        <Select
                            value={itemsPerPage}
                            onChange={handleItemsPerPageChange}
                            label="Itens por página"
                            sx={{ borderRadius: '4px' }}
                        >
                            {[10, 20, 50, 100].map(value => <MenuItem key={value} value={value}>{value}</MenuItem>)}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} sm={2}>
                    <FormControl fullWidth variant="outlined" size="small">
                        <InputLabel>Filtrar por ano</InputLabel>
                        <Select
                            value={selectedYear}
                            onChange={handleYearChange}
                            label="Filtrar por ano"
                            sx={{ borderRadius: '4px' }}
                        >
                            <MenuItem value="">Todos os anos</MenuItem>
                            {uniqueYears.map(year => <MenuItem key={year} value={year}>{year}</MenuItem>)}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} sm={2}>
                    <FormControl fullWidth variant="outlined" size="small">
                        <InputLabel>Filtrar por mês</InputLabel>
                        <Select
                            value={selectedMonth}
                            onChange={handleMonthChange}
                            label="Filtrar por mês"
                            sx={{ borderRadius: '4px' }}
                        >
                            {months.map(month => <MenuItem key={month.value} value={month.value}>{month.label}</MenuItem>)}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} sm={3}>
                    <FormControl fullWidth variant="outlined" size="small">
                        <InputLabel>Filtrar por modalidade</InputLabel>
                        <Select
                            value={selectedModality}
                            onChange={handleModalityChange}
                            label="Filtrar por modalidade"
                            sx={{ borderRadius: '4px' }}
                        >
                            <MenuItem value="">Todas as modalidades</MenuItem>
                            {modalidadeConst.map((mod) => (
                                <MenuItem key={mod} value={mod}>{mod}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
            </Grid>

            {!displayedData.length && (
                <Typography variant="body2" color="textSecondary" align="center" sx={{ py: 4 }}>
                    Nenhum documento encontrado.
                </Typography>
            )}

            {/* Tabela estilo Gov (Bordas limpas, sem exageros de arredondamento) */}
            <TableContainer
                component={Paper}
                sx={{
                    borderRadius: '6px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                    border: '1px solid #dcdcdc',
                    overflowX: 'auto'
                }}
            >
                <Table sx={{ minWidth: 700 }}>
                    <TableHead sx={{ backgroundColor: govHeaderColor }}>
                        <TableRow>
                            <TableCell sx={{ color: '#fff', fontWeight: 'bold', fontSize: '0.85rem' }}>Nº</TableCell>
                            <TableCell sx={{ color: '#fff', fontWeight: 'bold', fontSize: '0.85rem' }}>Modalidade</TableCell>
                            <TableCell sx={{ color: '#fff', fontWeight: 'bold', fontSize: '0.85rem' }}>Descrição / Título</TableCell>
                            {displayedData.some(item => item.ano) && <TableCell sx={{ color: '#fff', fontWeight: 'bold', fontSize: '0.85rem' }}>Ano</TableCell>}
                            <TableCell sx={{ color: '#fff', fontWeight: 'bold', fontSize: '0.85rem' }}>Arquivos Disponíveis</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {displayedData.map((row, index) => (
                            <TableRow
                                key={row.id}
                                hover
                                sx={{
                                    backgroundColor: index % 2 === 0 ? '#ffffff' : '#f9fbfd',
                                    '&:last-child td, &:last-child th': { border: 0 },
                                    borderBottom: '1px solid #eaeaea'
                                }}
                            >
                                <TableCell sx={{ fontWeight: '600', whiteSpace: 'nowrap', color: '#333' }}>{row.numero?.toUpperCase() || '-'}</TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.85rem', color: '#444' }}>{row?.modalidade || '-'}</TableCell>
                                <TableCell sx={{ maxWidth: '300px' }}>
                                    <Typography variant="body2" sx={{ fontWeight: '600', color: '#1a1a1a', fontSize: '0.85rem' }}>
                                        {row.title?.toUpperCase()}
                                    </Typography>
                                    <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5, lineHeight: 1.2 }}>
                                        {row.descricao?.toUpperCase()}
                                    </Typography>
                                </TableCell>
                                {row.ano && <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.85rem' }}>{row.ano}</TableCell>}

                                <TableCell sx={{ minWidth: '280px' }}>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8, py: 1 }}>
                                        {(() => {
                                            const documentosPrincipais = Object.keys(docLabels)
                                                .map((key) => {
                                                    const link = row.hrefs?.[key];

                                                    if (!link) return null;

                                                    return {
                                                        key,
                                                        nome: docLabels[key],
                                                        link
                                                    };
                                                })
                                                .filter(Boolean);

                                            const documentosVisiveis = documentosPrincipais.slice(0, 2);

                                            const documentosRestantes = [
                                                ...documentosPrincipais.slice(2),
                                                ...(Array.isArray(row.hrefs?.outros) ? row.hrefs.outros : [])
                                            ];

                                            return (
                                                <>
                                                    {documentosVisiveis.map((documento) => (
                                                        <Button
                                                            key={documento.key}
                                                            size="small"
                                                            variant="contained"
                                                            href={documento.link}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            sx={{
                                                                fontSize: '0.7rem',
                                                                fontWeight: '700',
                                                                px: 1.2,
                                                                py: 0.4,
                                                                textTransform: 'none',
                                                                borderRadius: '4px',
                                                                backgroundColor: govHeaderColor,
                                                                boxShadow: 'none',
                                                                '&:hover': {
                                                                    backgroundColor: '#0c326f',
                                                                    boxShadow: 'none'
                                                                }
                                                            }}
                                                        >
                                                            {documento.nome}
                                                        </Button>
                                                    ))}

                                                    {documentosRestantes.length > 0 && (
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            onClick={() =>
                                                                handleOutrosClick(
                                                                    documentosRestantes,
                                                                    row.title
                                                                )
                                                            }
                                                            sx={{
                                                                fontSize: '0.7rem',
                                                                fontWeight: '700',
                                                                px: 1.2,
                                                                py: 0.4,
                                                                textTransform: 'none',
                                                                borderRadius: '4px',
                                                                borderColor: govHeaderColor,
                                                                color: govHeaderColor,
                                                                borderWidth: '1px',
                                                                '&:hover': {
                                                                    borderColor: '#0c326f',
                                                                    backgroundColor: 'rgba(19, 81, 180, 0.04)',
                                                                    borderWidth: '1px'
                                                                }
                                                            }}
                                                        >
                                                            OUTROS ({documentosRestantes.length})
                                                        </Button>
                                                    )}
                                                </>
                                            );
                                        })()}
                                    </Box>
                                </TableCell>


                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Paginação */}
            <Box display="flex" justifyContent="center" mt={3}>
                <Pagination
                    count={Math.ceil(data.length / itemsPerPage)}
                    page={currentPage}
                    onChange={handlePageChange}
                    color="primary"
                    shape="rounded"
                />
            </Box>

            {/* Dialog / Pop-up institucional para múltiplos arquivos */}
            <Dialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: '6px', p: 1 } }}
            >
                <DialogTitle sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1.5, color: '#1a1a1a', fontSize: '1rem' }}>
                    <FolderIcon sx={{ color: govHeaderColor }} /> Documentos Adicionais (Outros)
                </DialogTitle>
                <Divider />
                <DialogContent>
                    <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 2, fontWeight: '600' }}>
                        Processo: {dialogTitle}
                    </Typography>
                    <List sx={{ bgcolor: '#f8f9fa', borderRadius: '4px', p: 1, maxHeight: '350px', overflowY: 'auto', border: '1px solid #e0e0e0' }}>
                        {dialogFiles.map((file, idx) => (
                            <ListItem
                                key={idx}
                                button
                                component="a"
                                href={file.link}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                    borderRadius: '4px',
                                    mb: 1,
                                    bgcolor: '#fff',
                                    border: '1px solid #dcdcdc',
                                    '&:hover': { bgcolor: '#f0f4f8', borderColor: govHeaderColor },
                                    transition: 'all 0.2s ease'
                                }}
                            >
                                <ListItemIcon sx={{ minWidth: '36px' }}>
                                    <FileIcon sx={{ color: govHeaderColor }} fontSize="small" />
                                </ListItemIcon>
                                <ListItemText
                                    primary={file.nome ? file.nome.toUpperCase() : `DOCUMENTO ${idx + 1}`}
                                    secondary="Clique para abrir ou baixar"
                                    primaryTypographyProps={{ sx: { fontWeight: '600', fontSize: '0.85rem', color: '#333' } }}
                                    secondaryTypographyProps={{ sx: { fontSize: '0.7rem' } }}
                                />
                                <OpenIcon fontSize="small" sx={{ color: '#666' }} />
                            </ListItem>
                        ))}
                    </List>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={() => setDialogOpen(false)}
                        variant="contained"
                        sx={{
                            borderRadius: '4px',
                            fontWeight: 'bold',
                            textTransform: 'none',
                            backgroundColor: govHeaderColor,
                            boxShadow: 'none',
                            '&:hover': { backgroundColor: '#0c326f', boxShadow: 'none' }
                        }}
                    >
                        Fechar
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}